import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { useSelector } from "react-redux";
import { type RootState } from "src/common/rootState.type";

import { useReferralFee } from "../hooks/useReferralFee";
import { useFbaFee } from "../hooks/useFbaFee";
import { useStorageFee } from "../hooks/useStorageFee";
import { FbaPresenter } from "./FbaPresenter";

export interface FbaInputs {
  monthType: number;
  sellPrice: number | undefined;
  deliveryCost: number | undefined;
  stockCnt: number;
  estimatedCnt: number;
  costPrice: number | undefined;
  otherCost: number | undefined;
  sellCnt: number;
}

/**
 * FBA Logicコンポーネント
 */
export const FbaContainer: React.FC = () => {
  // 状態管理
  const { product } = useSelector((state: RootState) => state.top);

  // LoadingのState管理
  const [isLoading] = useState(false);
  // FBA手数料（通常 + 冷凍食品手数料の内訳）
  const {
    fbaFee,
    freezingFee,
    updateBySellPrice: updateFbaFeeBySellPrice,
  } = useFbaFee();
  // FBA手数料の合計（利益計算に使う）
  const totalFbaFee =
    fbaFee === undefined && freezingFee === undefined
      ? undefined
      : (fbaFee ?? 0) + (freezingFee ?? 0);
  // 在庫保管手数料（クライアント側計算）
  const { normalStorageFee, specialStorageFee } = useStorageFee();
  // 販売手数料の取得（税抜の販売手数料・カテゴリ別成約料 + 税込合計）
  const {
    referralFee,
    closingFee,
    referralFeeIncTax,
    isLoading: isReferralFeeLoading,
    hasError: hasReferralFeeError,
    updateBySellPrice: updateReferralFeeBySellPrice,
  } = useReferralFee();

  const { register, setValue, watch } = useForm<FbaInputs>({
    defaultValues: {
      monthType: 1,
      sellPrice: 0,
      deliveryCost: undefined,
      stockCnt: 1,
      estimatedCnt: 1,
      costPrice: undefined,
      otherCost: undefined,
      sellCnt: 1,
    },
  });

  const {
    costPrice,
    deliveryCost,
    estimatedCnt,
    otherCost,
    monthType,
    sellCnt,
    sellPrice,
    stockCnt,
  } = watch();

  // productの変更検知
  useEffect(() => {
    setValue("sellPrice", product?.actPrice);
    setValue("costPrice", product?.costPrice);
  }, [product]);

  /**
   * 在庫保管手数料の月タイプ変更時
   * @param type タイプ
   */
  const monthOnClick = (type: number): any => {
    setValue("monthType", type);
  };

  /**
   * 商品価格の変動時に発火
   * @param event
   */
  const sellPriceOnChange = (event: any): void => {
    const sellPrice = Number(event.target.value);
    setValue("sellPrice", sellPrice);
    updateFbaFeeBySellPrice(sellPrice);
    updateReferralFeeBySellPrice(sellPrice);
  };

  /**
   * 在庫保管手数料の計算（月タイプにより通常期 / 繁忙期を切替）
   */
  const getStorageFee = (): number | undefined => {
    return monthType === 1 ? normalStorageFee : specialStorageFee;
  };

  /**
   * 合計在庫保管手数料の計算
   */
  const getTotalStorageFee = (): number | undefined => {
    const fee = getStorageFee();
    if (fee === undefined) {
      return undefined;
    }
    return fee * stockCnt;
  };

  /**
   * 1商品あたりの在庫保管手数料の計算
   */
  const getEstimatedFeePerItem = (): number | undefined => {
    const total = getTotalStorageFee();
    if (total === undefined) {
      return undefined;
    }
    return total / Number(estimatedCnt);
  };

  /**
   * その他の費用の合計の計算
   */
  const getTotalOther = (): number => {
    return Number(otherCost);
  };

  /**
   * 損益分岐価格の計算
   */
  const getBreakEvenPrice = (): number | undefined => {
    if (
      referralFeeIncTax === undefined ||
      getEstimatedFeePerItem() === undefined ||
      totalFbaFee === undefined
    ) {
      return undefined;
    } else {
      return (
        Number(deliveryCost) +
        Number(referralFeeIncTax) +
        Number(getEstimatedFeePerItem()) +
        Number(totalFbaFee) +
        getTotalOther() +
        Number(costPrice ?? 0)
      );
    }
  };

  /**
   * 純利益額の計算
   */
  const getProfit = (): number | undefined => {
    if (getBreakEvenPrice() === undefined) {
      return undefined;
    } else {
      return (
        (Number(sellPrice) - Number(getBreakEvenPrice())) * Number(sellCnt)
      );
    }
  };

  /**
   * 純利益率の計算
   */
  const getProfitRate = (): number | undefined => {
    return (
      ((Number(sellPrice) - Number(getBreakEvenPrice())) / Number(sellPrice)) *
      100
    );
  };

  const args = {
    register,
    fbaFee,
    freezingFee,
    sellPriceOnChange,
    referralFee,
    closingFee,
    referralFeeIncTax,
    isReferralFeeLoading,
    hasReferralFeeError,
    getTotalStorageFee,
    getEstimatedFeePerItem,
    getStorageFee,
    monthOnClick,
    monthType,
    getTotalOther,
    getBreakEvenPrice,
    getProfit,
    getProfitRate,
    isLoading,
  };

  return <FbaPresenter {...args} />;
};
